import { Button, DatePicker, Typography, Row, Col, Checkbox, Switch, Tag, Select, Segmented } from "antd";
import "../../Styles/styles-Reports.css";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { getTeamDatedDrills } from "../../store/actions/team.actions.js";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { useAuth0 } from "@auth0/auth0-react";
import { get_metric_info_from_identifier } from "../../Utils/chart_utils";
import { FLAG_GROUP_BY_OPTIONS, FLAG_GROUP_BY_POSITION, IGeneralReportsConfig, PLAYER_REPORT_DATA_STREAMS, REPORT_TYPES } from "./constants";
import { useDataAccess } from "@components/PermissionedComponents";
import { LockOutlined } from "@ant-design/icons";
import { getUserDefinedFlags } from "@store/actions/userDefinedFlags.actions";
import { buildFlagTypeOptions } from "@components/StatusTrackerV2/mappers/flags.mapper";
import { isStaffEmail } from "@utils/user_utils";
import './css/GeneralReportConfig.css'
import PlayerOptions from "@components/Common/PlayerOptions";
import { useMetricDropdowns } from "../../hooks/useDropdownInitialization";
import { getExpectedMetricCount, isMetricSlotFilled } from "@utils/report_metric_utils";
import { useEffect, useMemo } from "react";

const { Text } = Typography;
const { Option } = Select;

export const GeneralReportConfig = ({
    selectedReportTypeKey,
    getSelectReportTypeObj,
    config,
    setConfig,
    setMetricSelectorModalIsOpen,
    setMetricSelectorPrefix,
    setUseAggs
}: {
    selectedReportTypeKey?: string,
    getSelectReportTypeObj: (key?: string) => any,
    config: IGeneralReportsConfig,
    setConfig: React.Dispatch<React.SetStateAction<IGeneralReportsConfig>>,
    setMetricSelectorModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    setMetricSelectorPrefix: React.Dispatch<React.SetStateAction<string | undefined>>,
    setUseAggs: React.Dispatch<React.SetStateAction<boolean>>,
}) => {
    const {
        all_metric_types: allMetricTypes,
        positions,
        playerGroups,
        drills,
        datedDrills,
        allPlayers,
        misc: { comparison_types },
        info: {
            coach_list: coach_list,
            display_name: teamName
        },
        playersStatus,
        dropdowns: { forceplate: forceplateDropdowns, testing: testingDropdowns, nutrition: nutritionDropdowns, cognition: cognitionDropdowns }
    } = useSelector(({ team }) => team);

    // A report must not be configurable over a stream the user cannot read.
    const { canAccess } = useDataAccess();

    // Ensure dropdowns are loaded for player_date_range_report
    useMetricDropdowns(
        selectedReportTypeKey === 'player_date_range_report' ? ['testing', 'forceplate', 'nutrition', 'cognition']
            : selectedReportTypeKey === 'percent_of_best_analysis' ? ['testing', 'forceplate']
                : []
    );

    const dispatch = useDispatch();
    const { user } = useAuth0();

    // The same options, values and source as the tracker's flag filter: one entry each for
    // GPS, Forceplate and Testing, then every active user-defined flag (logic and coach).
    const udfFlags = useSelector(({ userDefinedFlags }: any) => userDefinedFlags?.flags || []);
    const flagOptions = useMemo(() => buildFlagTypeOptions(udfFlags), [udfFlags]);
    const needsFlags = !!getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('flag_filter');
    // The backend refuses the flags (403) when the team does not have the feature and the user is not staff.
    const udfEnabled = useSelector(({ team }: any) => team.info?.user_defined_flags_enabled ?? false) || isStaffEmail(user?.email);
    useEffect(() => {
        if (needsFlags && udfEnabled && udfFlags.length === 0) dispatch(getUserDefinedFlags() as any);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsFlags, udfEnabled]);
    // The report always bands by something, so seed the default as soon as the report
    // type asks for it. The payload spreads the config, so the value always reaches the API.
    const needsGroupBy = !!getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('group_by');
    useEffect(() => {
        if (needsGroupBy && !config.group_by) {
            setConfig({ ...config, group_by: FLAG_GROUP_BY_POSITION });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsGroupBy]);
    const needsDatedDrills = getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('dated_drill_filter');
    useEffect(() => {
        if (needsDatedDrills && config.date) {
            (dispatch(getTeamDatedDrills(config.date) as any) as Promise<void>)
                .catch(() => message.error("Failed to load drills for the selected date"));
            // date changed, previously selected drills may not exist on the new date
            setConfig((prev) => ({ ...prev, dated_drill_filter: [] }));
        }
    }, [needsDatedDrills, config.date]);

    const dateFormat = "YYYY-MM-DD";


    const getOrderedCoachEmailList = () => {
        let res = [{
            label: (<span>{user?.name} <span style={{ fontSize: 12 }}>({user?.email})</span></span>),
            value: user?.email,
            key: user?.email
        }];
        coach_list?.forEach((coach: any) => {
            if (coach.email === user?.email) return;
            res.push({
                label: (<span>{coach.first_name} {coach.last_name} <span style={{ fontSize: 12 }}>({coach.email})</span></span>),
                value: coach.email,
                key: coach.email
            })
        })
        return res;
    }

    const handleGroupByPositionChange = (value: any) => {
        if (value === true) {
            setConfig({ ...config, group_by_position: true });
        } else {
            if (Object.keys(config).includes('group_by_position')) {
                delete (config as any).group_by_position;
                setConfig({ ...config });
            }
        }
    }

    const handleSingleDatePickerChange = (date: any, dateString: any) => {
        setConfig({ ...config, date: dateString });
    }

    const handleDateRangePickerChange = (date: any, dateStrings: any) => {
        setConfig({ ...config, date_range: dateStrings });
    }

    const handleEmailSelectionChange = (checkedEmails: any) => {
        setConfig({ ...config, emails: checkedEmails });
    }

    const handlePlayerReportDataStreamChange = (checkedValues: any) => {
        setConfig({ ...config, player_report_data_streams: checkedValues });
    }

    const handleWeeklyStreamChange = (checkedValues: any) => {
        setConfig({ ...config, weekly_streams: checkedValues });
    }

    const handlePositionFilterChange = (value: any) => {
        setConfig({ ...config, position_filter: value });
    }

    const handlePlayerGroupFilterChange = (value: any) => {
        setConfig({ ...config, player_group_filter: value });
    }

    const handleGroupByChange = (value: any) => {
        setConfig({ ...config, group_by: value || FLAG_GROUP_BY_POSITION });
    }

    const handleFlagFilterChange = (value: any) => {
        setConfig({ ...config, flag_filter: value });
    }

    const handleDrillFilterChange = (value: any) => {
        setConfig({ ...config, drill_filter: value });
    }

    const handleDatedDrillFilterChange = (value: any) => {
        setConfig({ ...config, dated_drill_filter: value });
    }

    const handleSinglePlayerFilterChange = (value: any) => {
        setConfig({ ...config, player_filter: value });
    }

    const handleComparisonTypeChange = (value: any) => {
        setConfig({ ...config, comparison_type: value });
    }

    const handleCurrentModalType = (value: string) => {
        setConfig({ ...config, current_modal_type: value })
    }

    const handleDualDateRangesChange = (rangeIndex: number) => (date: any, dateStrings: any) => {
        const currentRanges = config.dual_date_ranges || [[], []];
        const newRanges: [string[], string[]] = [...currentRanges] as [string[], string[]];
        newRanges[rangeIndex] = dateStrings;
        setConfig({ ...config, dual_date_ranges: newRanges });
    }

    const handlePlayerFilterChangeForSubjects = (value: any) => {
        setConfig({ ...config, player_filter: value });
    }

    const handlePositionFilterChangeForSubjects = (value: any) => {
        setConfig({ ...config, position_filter: value });
    }

    const handlePlayerGroupFilterChangeForSubjects = (value: any) => {
        setConfig({ ...config, player_group_filter: value });
    }

    const handleAggregateOptionChange = (checked: boolean) => {
        setConfig({ ...config, aggregate_option: checked ? 'best' : 'avg' });
    }

    const handleAggregateColumnsChange = (value: any) => {
        // Limit to max 7 selections
        if (value.length <= 7) {
            setConfig({ ...config, aggregate_columns: value });
        }
    }

    const handlePlayerDateRangeMetricsChange = (value: any) => {
        // Limit to max 15 selections
        if (value.length <= 15) {
            setConfig({ ...config, [`player_date_range_report-metrics`]: value } as any);
        }
    }

    const getDisabledDateForWeekly = (current: any, info: any) => {
        if (selectedReportTypeKey !== 'weekly') {
            return false;
        }

        const { range } = info || {};

        if (!range) {
            return false;
        }

        const [start, end] = range;

        if (start && end) {
            // Both dates are selected, don't disable anything
            return false;
        }

        if (start && !end) {
            // Start date is selected, disable dates that would make range != 7 days
            const daysDiff = Math.abs(current.diff(start, 'days'));
            return daysDiff !== 6; // 6 days difference = 7 days total (inclusive)
        }

        if (!start && end) {
            // End date is selected, disable dates that would make range != 7 days
            const daysDiff = Math.abs(current.diff(end, 'days'));
            return daysDiff !== 6; // 6 days difference = 7 days total (inclusive)
        }

        return false;
    }

    const getFormattedMetricSelection = (prefix: any) => {
        // Unfilled slots are persisted alongside the filled ones so a half-finished
        // selection survives closing the modal — they must not be counted or labelled.
        const metric_list: { agg: 'best' | 'avg' | 'sum', metric: string }[] =
            ((config as any)[`${prefix}-metrics`] || []).filter(isMetricSlotFilled);

        const expected_total_count = getExpectedMetricCount(prefix);

        let names: any[] = [];

        metric_list.forEach((metric_tuple) => {
            const { metric_id: unique_identifier, metric_stat } = get_metric_info_from_identifier(metric_tuple.metric);

            const metric_type = unique_identifier.split('-')[0];
            const metric_obj = allMetricTypes?.[metric_type]?.find((x: any) => x.unique_identifier === unique_identifier);

            let cleaned_name = undefined;

            if (prefix === 'player_date_range_report') {
                cleaned_name = "";
            } else if (metric_tuple.agg === 'avg') {
                cleaned_name = "[A] "
            } else if (metric_tuple.agg === 'best') {
                cleaned_name = "[B] "
            } else {
                cleaned_name = "[C] "
            }

            if (metric_obj) {
                // Percent of Best is WYSIWYG: the selected metric renders as
                // itself, so never relabel a Left metric as "[Asym] ...".
                if (prefix !== 'percent_of_best_analysis' && metric_obj.name.includes("Left")) {
                    const asymName = metric_obj.name.split(" Left ")

                    cleaned_name += `[Asym] ${asymName[0]}${asymName[1] ? ` ${asymName[1]}` : ''}`
                } else {
                    cleaned_name += metric_obj.name;
                }
            } else {
                cleaned_name += unique_identifier;
            }

            if (cleaned_name) {
                names.push(cleaned_name);
            }
        })

        // For player_date_range_report, show 1-15 instead of total count
        const displayCount = prefix === 'player_date_range_report' ? '1-15' : expected_total_count;

        return (
            <>
                <Text style={{ marginRight: '5px' }}>
                    ({names.length}/{displayCount})
                </Text>
                {names.map((name, index) => (
                    <Tag style={{ borderRadius: '4px', marginRight: 2 }} key={index}>{name}</Tag>
                ))}
            </>
        )
    }

    // Build metric dropdown options for player_date_range_report (and 49ers briefing notes)
    const playerDateRangeMetricOptions = useMemo(() => {
        if (selectedReportTypeKey !== 'player_date_range_report' && selectedReportTypeKey !== 'percent_of_best_analysis') {
            return [];
        }

        // Percent of Best is WYSIWYG: offer every testing/forceplate metric
        // under its raw name, instead of the curated dropdowns whose
        // "[Asym] X" entries alias the Left member's identifier.
        if (selectedReportTypeKey === 'percent_of_best_analysis') {
            const buildRawOptions = (metrics: any[]) =>
                (metrics || [])
                    .filter((metric: any) => metric.visible === true || metric.visible === undefined)
                    .map((metric: any) => ({
                        label: metric.name,
                        value: metric.unique_identifier,
                    }))
                    .sort((a: any, b: any) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

            return [
                { label: 'Testing', options: buildRawOptions(allMetricTypes?.testing) },
                { label: 'Force Plate', options: buildRawOptions(allMetricTypes?.forceplate) },
            ].filter(g => g.options.length > 0);
        }

        const filteredMetricTypes = JSON.parse(JSON.stringify(allMetricTypes || {}));
        const forceplateLabelMap = new Map();
        const testingLabelMap = new Map();
        const nutritionLabelMap = new Map();
        const cognitionLabelMap = new Map();

        // Build forceplate label map
        if (forceplateDropdowns?.length > 0) {
            forceplateDropdowns.forEach((dropdownItem: any) => {
                if (dropdownItem.children) {
                    dropdownItem.children.forEach((child: any) => {
                        forceplateLabelMap.set(child.key, dropdownItem.label + " - " + child.label);
                    });
                } else {
                    forceplateLabelMap.set(dropdownItem.key, dropdownItem.label);
                }
            });
        }

        // Build testing label map
        if (testingDropdowns?.length > 0) {
            testingDropdowns.forEach((dropdownItem: any) => {
                if (dropdownItem.children) {
                    dropdownItem.children.forEach((child: any) => {
                        testingLabelMap.set(child.key, dropdownItem.label + " - " + child.label);
                    });
                } else {
                    testingLabelMap.set(dropdownItem.key, dropdownItem.label);
                }
            });
        }

        // Build nutrition label map
        if (nutritionDropdowns?.length > 0) {
            nutritionDropdowns.forEach((dropdownItem: any) => {
                if (dropdownItem.children) {
                    dropdownItem.children.forEach((child: any) => {
                        nutritionLabelMap.set(child.key, dropdownItem.label + " - " + child.label);
                    });
                } else {
                    nutritionLabelMap.set(dropdownItem.key, dropdownItem.label);
                }
            });
        }

        // Build cognition label map
        if (cognitionDropdowns?.length > 0) {
            cognitionDropdowns.forEach((dropdownItem: any) => {
                if (dropdownItem.children) {
                    dropdownItem.children.forEach((child: any) => {
                        cognitionLabelMap.set(child.key, dropdownItem.label + " - " + child.label);
                    });
                } else {
                    cognitionLabelMap.set(dropdownItem.key, dropdownItem.label);
                }
            });
        }

        // Filter forceplate metrics based on forceplateDropdowns
        if (filteredMetricTypes.forceplate && forceplateDropdowns?.length > 0) {
            const forceplateDropdownKeys = new Set();
            forceplateDropdowns.forEach((dropdownItem: any) => {
                if (dropdownItem.children) {
                    dropdownItem.children.forEach((child: any) => {
                        forceplateDropdownKeys.add(child.key);
                    });
                } else {
                    forceplateDropdownKeys.add(dropdownItem.key);
                }
            });
            filteredMetricTypes.forceplate = filteredMetricTypes.forceplate.filter((metric: any) =>
                forceplateDropdownKeys.has(metric.unique_identifier)
            );
        }

        // Filter testing metrics based on testingDropdowns
        if (filteredMetricTypes.testing && testingDropdowns?.length > 0) {
            const testingDropdownKeys = new Set();
            testingDropdowns.forEach((dropdownItem: any) => {
                if (dropdownItem.children) {
                    dropdownItem.children.forEach((child: any) => {
                        testingDropdownKeys.add(child.key);
                    });
                } else {
                    testingDropdownKeys.add(dropdownItem.key);
                }
            });
            filteredMetricTypes.testing = filteredMetricTypes.testing.filter((metric: any) =>
                testingDropdownKeys.has(metric.unique_identifier)
            );
        }

        // Filter nutrition metrics based on nutritionDropdowns
        if (filteredMetricTypes.nutrition && nutritionDropdowns?.length > 0) {
            const nutritionDropdownKeys = new Set();
            nutritionDropdowns.forEach((dropdownItem: any) => {
                if (dropdownItem.children) {
                    dropdownItem.children.forEach((child: any) => {
                        nutritionDropdownKeys.add(child.key);
                    });
                } else {
                    nutritionDropdownKeys.add(dropdownItem.key);
                }
            });
            filteredMetricTypes.nutrition = filteredMetricTypes.nutrition.filter((metric: any) =>
                nutritionDropdownKeys.has(metric.unique_identifier)
            );
        }

        // Filter cognition metrics based on cognitionDropdowns
        if (filteredMetricTypes.cognition && cognitionDropdowns?.length > 0) {
            const cognitionDropdownKeys = new Set();
            cognitionDropdowns.forEach((dropdownItem: any) => {
                if (dropdownItem.children) {
                    dropdownItem.children.forEach((child: any) => {
                        cognitionDropdownKeys.add(child.key);
                    });
                } else {
                    cognitionDropdownKeys.add(dropdownItem.key);
                }
            });
            filteredMetricTypes.cognition = filteredMetricTypes.cognition.filter((metric: any) =>
                cognitionDropdownKeys.has(metric.unique_identifier)
            );
        }

        const buildGroupOptions = (metrics: any[], labelMap: Map<string, string>) =>
            (metrics || [])
                .map((metric: any) => ({
                    label: labelMap.get(metric.unique_identifier) ?? metric.name,
                    value: metric.unique_identifier,
                }))
                .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

        const groups = [
            { label: 'Testing', options: buildGroupOptions(filteredMetricTypes?.testing, testingLabelMap) },
            { label: 'Force Plate', options: buildGroupOptions(filteredMetricTypes?.forceplate, forceplateLabelMap) },
            { label: 'Nutrition', options: buildGroupOptions(filteredMetricTypes?.nutrition, nutritionLabelMap) },
            { label: 'Cognition', options: buildGroupOptions(filteredMetricTypes?.cognition, cognitionLabelMap) },
        ].filter(g => g.options.length > 0);

        return groups;
    }, [selectedReportTypeKey, allMetricTypes, forceplateDropdowns, testingDropdowns, nutritionDropdowns, cognitionDropdowns]);

    return (
        <div
            style={{
                backgroundColor: '#fafafa',
                padding: '10px',
                borderRadius: '8px',
                marginTop: '10px',
                display: selectedReportTypeKey ? 'flex' : 'none',
                flexDirection: 'column',
                gap: '10px',
            }}
        >
            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('single_player_filter') ? (
                <div className="general-report-position-filter">
                    <Text> Please select a player for the report:</Text>
                    <Select
                        showSearch
                        optionFilterProp="label"
                        allowClear
                        placeholder="Select Player"
                        onChange={handleSinglePlayerFilterChange}
                        value={config.player_filter}
                        style={{ width: '200px' }}
                        filterOption={(input, option) => {
                            const label = option?.label;
                            return typeof label === 'string' && label.toLowerCase()?.includes(input?.toLowerCase())
                        }}
                    >
                        {allPlayers
                            .filter((e: any) => e.retired === false)
                            .map((player: any, i: number) => {
                                return PlayerOptions({ player, teamName, index: i, playersStatus });
                            })
                        }
                    </Select>
                </div>
            ) : null}


            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('date') ? (
                <div>
                    <Text>Please pick a date for the report:</Text>
                    <DatePicker
                        style={{ borderRadius: '8px', marginLeft: '10px' }}
                        onChange={handleSingleDatePickerChange}
                        value={config.date ? dayjs(config.date, dateFormat) : null}
                        format={dateFormat}
                        placeholder="Select Date"
                        allowClear
                    />
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('date_range') ? (
                <div>
                    <Text>Please pick a date range for the report:</Text>
                    <DatePicker.RangePicker
                        style={{ borderRadius: '8px', marginLeft: '10px' }}
                        onChange={handleDateRangePickerChange}
                        value={config.date_range ? [config.date_range[0] ? dayjs(config.date_range[0], dateFormat) : null, config.date_range[1] ? dayjs(config.date_range[1], dateFormat) : null] : null}
                        format={dateFormat}
                        allowClear={false}
                        disabledDate={selectedReportTypeKey === 'weekly' ? getDisabledDateForWeekly : undefined}
                    />
                    {selectedReportTypeKey === 'single_week_distance_breakdown' && <Text type="secondary" style={{ marginLeft: '5px' }}>(Max 7 days)</Text>}
                    {selectedReportTypeKey === 'multi_week_distance_breakdown' && <Text type="secondary" style={{ marginLeft: '5px' }}>(Max 7 weeks)</Text>}
                    {selectedReportTypeKey === 'status_report' && <Text type="secondary" style={{ marginLeft: '5px' }}>(Max 9 months or 266 days)</Text>}
                    {selectedReportTypeKey === 'weekly_body_weight_report' && <Text type="secondary" style={{ marginLeft: '5px' }}>(Max 12 weeks)</Text>}
                    {selectedReportTypeKey === 'report-card-bulk' && <Text type="secondary" style={{ marginLeft: '5px' }}>(Max 90 days)</Text>}
                    {selectedReportTypeKey === 'weekly' && <Text type="secondary" style={{ marginLeft: '5px' }}>(Must be exactly 7 days)</Text>}
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('comparison') ? (
                <div className="general-report-position-filter">
                    <Text> Please select a type of comparison :</Text>
                    <Select
                        showSearch
                        optionFilterProp="value"
                        allowClear
                        onChange={handleComparisonTypeChange}
                        value={config.comparison_type || 'latest_vs_prev'}
                        options={comparison_types}
                        filterOption={(input, option) => {
                            const label = option?.label;
                            return typeof label === 'string' && label.toLowerCase()?.includes(input?.toLowerCase())
                        }}
                        style={{ width: '250px' }}
                    />
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('position_filter') ? (
                <div className="general-report-position-filter">
                    <Text> Please select the positions to be included in the report (Optional):</Text>
                    <Select
                        showSearch
                        optionFilterProp="value"
                        allowClear
                        placeholder="All Positions"
                        mode="multiple"
                        onChange={handlePositionFilterChange}
                        value={config.position_filter || []}
                    >
                        {positions.map((pos: any, i: any) => (
                            <Option key={i} value={pos}>
                                {pos}
                            </Option>
                        ))}
                    </Select>
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('player_group_filter') ? (
                <div className="general-report-position-filter">
                    <Text> Please select the player groups to be included in the report (Optional):</Text>
                    <Select
                        showSearch
                        optionFilterProp="value"
                        allowClear
                        placeholder="All Players"
                        mode="multiple"
                        onChange={handlePlayerGroupFilterChange}
                        value={config.player_group_filter || []}
                        options={
                            playerGroups.map((pl_group: any) => ({
                                label: pl_group.name,
                                value: pl_group.player_group_id,
                            }))
                        }
                        filterOption={(input, option) => {
                            const label = option?.label;
                            return typeof label === 'string' && label.toLowerCase()?.includes(input?.toLowerCase())
                        }}
                    />
                </div>
            ) : null}

            {needsFlags ? (
                <div className="general-report-position-filter">
                    <Text> Please select the flags to be included in the report (Optional):</Text>
                    <Select
                        showSearch
                        optionFilterProp="label"
                        allowClear
                        placeholder="All Flags"
                        mode="multiple"
                        onChange={handleFlagFilterChange}
                        value={config.flag_filter || []}
                        options={flagOptions}
                        filterOption={(input, option) => {
                            const label = option?.label;
                            return typeof label === 'string' && label.toLowerCase()?.includes(input?.toLowerCase())
                        }}
                    />
                </div>
            ) : null}

            {needsGroupBy ? (
                <div className="general-report-position-filter">
                    <Text> Please select how to group the report:</Text>
                    <Segmented
                        onChange={handleGroupByChange}
                        value={config.group_by || FLAG_GROUP_BY_POSITION}
                        options={FLAG_GROUP_BY_OPTIONS}
                    />
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('drill_filter') ? (
                <div className="general-report-position-filter">
                    <Text> Please select the drills to be included in the report (Optional):</Text>
                    <Select
                        showSearch
                        allowClear
                        placeholder="All Drills"
                        mode="multiple"
                        onChange={handleDrillFilterChange}
                        value={config.drill_filter || []}
                        options={
                            drills
                                .map((drill: any) => ({
                                    label: drill.name,
                                    value: drill.drill_id,
                                }))
                                .sort((a: any, b: any) => (a.label || '').localeCompare(b.label || ''))
                        }
                        filterOption={(input, option) => {
                            const label = option?.label;
                            return typeof label === 'string' && label.toLowerCase()?.includes(input?.toLowerCase())
                        }}
                    />
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('dated_drill_filter') ? (
                <div className="general-report-position-filter">
                    <Text> Please select the drills to be included in the report (Optional):</Text>
                    <Select
                        showSearch
                        allowClear
                        placeholder={config.date ? "All Drills" : "Select a date first"}
                        disabled={!config.date}
                        mode="multiple"
                        onChange={handleDatedDrillFilterChange}
                        value={config.dated_drill_filter || []}
                        options={
                            datedDrills
                                .map((drill: any) => ({
                                    label: drill.name,
                                    value: drill.drill_id,
                                }))
                                .sort((a: any, b: any) => (a.label || '').localeCompare(b.label || ''))
                        }
                        filterOption={(input, option) => {
                            const label = option?.label;
                            return typeof label === 'string' && label.toLowerCase()?.includes(input?.toLowerCase())
                        }}
                    />
                </div>
            ) : null}


            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('group_by_position') ? (
                <div>
                    <Text> Group by Position:</Text>

                    <Switch
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                        checked={config.group_by_position || false}
                        onChange={handleGroupByPositionChange}
                        style={{ marginLeft: '10px' }}
                    />
                </div>
            ) : null}


            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('player_report_type_selector') ? (
                <div>
                    <Text> Select data streams to include in the report:</Text><br />

                    <Checkbox.Group
                        value={config.player_report_data_streams || []}
                        onChange={handlePlayerReportDataStreamChange}
                    >
                        <Col >
                            {PLAYER_REPORT_DATA_STREAMS.map((item, idx) => (
                                <Row key={`${item.key}-${idx}`}>
                                    {/* Locked unless every stream the option needs is permitted —
                                        the report would otherwise 403 partway through. */}
                                    <Checkbox
                                        value={item.value}
                                        style={{ marginLeft: '10px' }}
                                        disabled={!(item.types || []).every((t: any) => canAccess(t))}
                                    >
                                        {item.label}
                                        {!(item.types || []).every((t: any) => canAccess(t)) && (
                                            <LockOutlined style={{ marginLeft: 4 }} />
                                        )}
                                        {
                                            config?.player_report_data_streams?.includes(item.key) ? (
                                                <>
                                                    <div style={{ padding: '2px', margin: '5px 0', borderRadius: '4px', border: '1px solid #ccc', marginTop: '5px' }}>
                                                        {getFormattedMetricSelection(item.key)}
                                                        <Button
                                                            size="small"
                                                            type="link"
                                                            onClick={() => {
                                                                setMetricSelectorPrefix(item.key);
                                                                handleCurrentModalType(item.key)

                                                                const playerReport = REPORT_TYPES.find(x => x.key === 'player_report');
                                                                if (playerReport?.metric_selector_info) {
                                                                    playerReport.metric_selector_info.count = item.count;
                                                                    playerReport.metric_selector_info.types = item.types;
                                                                }

                                                                if (item.key === 'report-card') {
                                                                    setUseAggs(true);
                                                                } else {
                                                                    setUseAggs(false);
                                                                }
                                                                setMetricSelectorModalIsOpen(true)
                                                            }
                                                            }
                                                        >
                                                            <u>Click here to set metrics</u>
                                                        </Button>
                                                    </div>
                                                </>) : null
                                        }

                                    </Checkbox>
                                </Row>
                            ))}
                        </Col>
                    </Checkbox.Group>


                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('metric_selector') ? (
                selectedReportTypeKey === 'player_date_range_report' ? (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Text>Select metrics to include in the report:</Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>(Max 15)</Text>
                        </div>
                        <div style={{ marginTop: '5px' }}>
                            <Select
                                showSearch
                                optionFilterProp="label"
                                allowClear
                                placeholder="Select metrics"
                                mode="multiple"
                                onChange={handlePlayerDateRangeMetricsChange}
                                value={(config as any)['player_date_range_report-metrics'] || []}
                                style={{ width: '100%', maxWidth: '600px' }}
                                options={playerDateRangeMetricOptions}
                                filterOption={(input, option) => {
                                    const label = option?.label;
                                    return typeof label === 'string' && label.toLowerCase()?.includes(input?.toLowerCase())
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <div>
                        <Text>Metrics to include in the report: </Text>
                        {getFormattedMetricSelection(getSelectReportTypeObj(selectedReportTypeKey).key)}
                        <Button
                            size="small"
                            type="link"
                            onClick={() => {
                                setMetricSelectorPrefix(getSelectReportTypeObj(selectedReportTypeKey).key);
                                setMetricSelectorModalIsOpen(true)
                                handleCurrentModalType(getSelectReportTypeObj(selectedReportTypeKey).key)
                                const reportObj = getSelectReportTypeObj(selectedReportTypeKey);
                                if (reportObj?.metric_selector_info?.useAggs) {
                                    setUseAggs(true);
                                } else {
                                    setUseAggs(false);
                                }
                            }
                            }
                        >
                            <u>Click here to configure metrics</u>
                        </Button>

                    </div>
                )
            ) : null}

            {/* ponytail: hyper-specific exception for the 49ers percent of best report — do not generalize */}
            {selectedReportTypeKey === 'percent_of_best_analysis' ? (
                <div>
                    <Checkbox
                        checked={config.show_briefing_notes || false}
                        onChange={(e) => setConfig({ ...config, show_briefing_notes: e.target.checked, briefing_notes_metrics: e.target.checked ? config.briefing_notes_metrics : [] })}
                    >
                        Show briefing notes
                    </Checkbox>
                    {config.show_briefing_notes ? (
                        <div style={{ marginTop: '5px' }}>
                            <Text>Select metrics for briefing notes:</Text>
                            <div style={{ marginTop: '5px' }}>
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    allowClear
                                    placeholder="Select metrics"
                                    mode="multiple"
                                    onChange={(value: string[]) => setConfig({ ...config, briefing_notes_metrics: value })}
                                    value={config.briefing_notes_metrics || []}
                                    style={{ width: '100%', maxWidth: '600px' }}
                                    options={playerDateRangeMetricOptions}
                                    filterOption={(input, option) => {
                                        const label = option?.label;
                                        return typeof label === 'string' && label.toLowerCase()?.includes(input?.toLowerCase())
                                    }}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('weekly_metric_selectors') ? (
                <div>
                    <div style={{ marginBottom: '12px' }}>
                        <Text>Select weekly data streams to include:</Text><br />
                        <Checkbox.Group
                            value={(config as any).weekly_streams || []}
                            onChange={handleWeeklyStreamChange}
                        >
                            {[
                                { value: 'gps', label: 'GPS' },
                                { value: 'testing', label: 'Testing' },
                                { value: 'forceplate', label: 'Forceplate' },
                                { value: 'wellness', label: 'Wellness' },
                            ].map(({ value, label }) => (
                                <Row key={value}>
                                    <Checkbox
                                        value={value}
                                        style={{ marginLeft: '10px' }}
                                        disabled={!canAccess(value as any)}
                                    >
                                        {label}
                                        {!canAccess(value as any) && (
                                            <LockOutlined style={{ marginLeft: 4 }} />
                                        )}
                                    </Checkbox>
                                </Row>
                            ))}
                        </Checkbox.Group>
                    </div>

                    {(config as any).weekly_streams?.includes('gps') && (
                        <div style={{ marginBottom: '15px' }}>
                            <Text>GPS Metrics (4 required): </Text>
                            {getFormattedMetricSelection('gps')}
                            <Button
                                size="small"
                                type="link"
                                onClick={() => {
                                    setMetricSelectorPrefix('gps');
                                    setMetricSelectorModalIsOpen(true);
                                    setUseAggs(true);
                                    handleCurrentModalType('gps');

                                    const weeklyReport = REPORT_TYPES.find(x => x.key === 'weekly');
                                    if (weeklyReport?.metric_selector_info) {
                                        weeklyReport.metric_selector_info.count = 4;
                                        weeklyReport.metric_selector_info.types = ['gps'];
                                    }
                                }}
                            >
                                <u>Click here to configure GPS metrics</u>
                            </Button>
                        </div>
                    )}

                    {(config as any).weekly_streams?.includes('testing') && (
                        <div style={{ marginBottom: '15px' }}>
                            <Text>Testing Metrics (4 required): </Text>
                            {getFormattedMetricSelection('testing')}
                            <Button
                                size="small"
                                type="link"
                                onClick={() => {
                                    setMetricSelectorPrefix('testing');
                                    setMetricSelectorModalIsOpen(true);
                                    setUseAggs(true);
                                    handleCurrentModalType('testing');

                                    const weeklyReport = REPORT_TYPES.find(x => x.key === 'weekly');
                                    if (weeklyReport?.metric_selector_info) {
                                        weeklyReport.metric_selector_info.count = 4;
                                        weeklyReport.metric_selector_info.types = ['testing'];
                                    }
                                }}
                            >
                                <u>Click here to configure testing metrics</u>
                            </Button>
                        </div>
                    )}

                    {(config as any).weekly_streams?.includes('forceplate') && (
                        <div style={{ marginBottom: '15px' }}>
                            <Text>Forceplate Metrics (4 required): </Text>
                            {getFormattedMetricSelection('forceplate')}
                            <Button
                                size="small"
                                type="link"
                                onClick={() => {
                                    setMetricSelectorPrefix('forceplate');
                                    setMetricSelectorModalIsOpen(true);
                                    setUseAggs(true);
                                    handleCurrentModalType('forceplate');

                                    const weeklyReport = REPORT_TYPES.find(x => x.key === 'weekly');
                                    if (weeklyReport?.metric_selector_info) {
                                        weeklyReport.metric_selector_info.count = 4;
                                        weeklyReport.metric_selector_info.types = ['forceplate'];
                                    }
                                }}
                            >
                                <u>Click here to configure forceplate metrics</u>
                            </Button>
                        </div>
                    )}
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('dual_date_ranges') ? (
                <div>
                    <Text>Please pick two date ranges for comparison:</Text>
                    <div style={{ marginTop: '10px', marginLeft: '10px' }}>
                        <div style={{ marginBottom: '10px' }}>
                            <Text strong style={{ marginRight: '10px' }}>Date Range 1:</Text>
                            <DatePicker.RangePicker
                                style={{ borderRadius: '8px' }}
                                onChange={handleDualDateRangesChange(0)}
                                value={config.dual_date_ranges?.[0] ? [config.dual_date_ranges[0][0] ? dayjs(config.dual_date_ranges[0][0], dateFormat) : null, config.dual_date_ranges[0][1] ? dayjs(config.dual_date_ranges[0][1], dateFormat) : null] : null}
                                format={dateFormat}
                                allowClear={false}
                            />
                        </div>
                        <div>
                            <Text strong style={{ marginRight: '10px' }}>Date Range 2:</Text>
                            <DatePicker.RangePicker
                                style={{ borderRadius: '8px' }}
                                onChange={handleDualDateRangesChange(1)}
                                value={config.dual_date_ranges?.[1] ? [config.dual_date_ranges[1][0] ? dayjs(config.dual_date_ranges[1][0], dateFormat) : null, config.dual_date_ranges[1][1] ? dayjs(config.dual_date_ranges[1][1], dateFormat) : null] : null}
                                format={dateFormat}
                                allowClear={false}
                            />
                        </div>
                    </div>
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('subjects_filter') ? (
                <div>
                    <Text>Select subjects (at least one required):</Text>
                    
                    <div className="general-report-position-filter" style={{ marginTop: '10px' }}>
                        <Text>Players (Optional):</Text>
                        <Select
                            showSearch
                            optionFilterProp="label"
                            allowClear
                            placeholder="Select players or Full Team"
                            mode="multiple"
                            onChange={handlePlayerFilterChangeForSubjects}
                            value={config.player_filter || []}
                            style={{ width: '100%', maxWidth: '600px' }}
                            filterOption={(input, option) => {
                                const label = option?.label;
                                return typeof label === 'string' && label.toLowerCase()?.includes(input?.toLowerCase())
                            }}
                        >
                            <Option key="full-team" value="full-team" label="Full Team">
                                Full Team
                            </Option>
                            {allPlayers
                                .filter((e: any) => e.retired === false)
                                .map((player: any, i: number) => {
                                    return (
                                        <Option 
                                            key={`player-${player.player_id}`} 
                                            value={player.player_id}
                                            label={`${player.first_name} ${player.last_name}`}
                                        >
                                            {player.first_name} {player.last_name}
                                        </Option>
                                    );
                                })
                            }
                        </Select>
                    </div>

                    <div className="general-report-position-filter" style={{ marginTop: '10px' }}>
                        <Text>Positions (Optional):</Text>
                        <Select
                            showSearch
                            optionFilterProp="value"
                            allowClear
                            placeholder="Select positions"
                            mode="multiple"
                            onChange={handlePositionFilterChangeForSubjects}
                            value={config.position_filter || []}
                            style={{ width: '100%', maxWidth: '600px' }}
                        >
                            {positions.map((pos: any, i: any) => (
                                <Option key={i} value={pos}>
                                    {pos}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div className="general-report-position-filter" style={{ marginTop: '10px' }}>
                        <Text>Player Groups (Optional):</Text>
                        <Select
                            showSearch
                            optionFilterProp="value"
                            allowClear
                            placeholder="Select player groups"
                            mode="multiple"
                            onChange={handlePlayerGroupFilterChangeForSubjects}
                            value={config.player_group_filter || []}
                            style={{ width: '100%', maxWidth: '600px' }}
                            options={
                                playerGroups.map((pl_group: any) => ({
                                    label: pl_group.name,
                                    value: pl_group.player_group_id,
                                }))
                            }
                            filterOption={(input, option) => {
                                const label = option?.label;
                                return typeof label === 'string' && label.toLowerCase()?.includes(input?.toLowerCase())
                            }}
                        />
                    </div>
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('aggregate_selector') ? (
                <div>
                    <Text>Select aggregate method:</Text>
                    <Switch
                        checkedChildren="Best"
                        unCheckedChildren="Avg"
                        checked={config.aggregate_option === 'best'}
                        onChange={handleAggregateOptionChange}
                        style={{ marginLeft: '10px' }}
                    />
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('aggregate_columns_selector') ? (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Text>Select aggregate columns to include in report:</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>(Max 7)</Text>
                    </div>
                    <div style={{ marginTop: '5px' }}>
                        <Select
                            showSearch
                            optionFilterProp="label"
                            allowClear
                            placeholder="Select aggregate columns"
                            mode="multiple"
                            onChange={handleAggregateColumnsChange}
                            value={config.aggregate_columns}
                            style={{ width: '100%', maxWidth: '600px' }}
                            filterOption={(input, option) => {
                                const label = option?.label;
                                return typeof label === 'string' && label.toLowerCase()?.includes(input?.toLowerCase())
                            }}
                        >
                            <Option value="player_value" label="Player Value">Player Value</Option>
                            <Option value="position_value" label="Position Value">Position Value</Option>
                            <Option value="team_value" label="Team Value">Team Value</Option>
                            <Option value="player_zscore" label="Player Z-Score">Player Z-Score</Option>
                            <Option value="player_pos_zscore" label="Player Position Z-Score">Player Position Z-Score</Option>
                            <Option value="player_team_zscore" label="Player Team Z-Score">Player Team Z-Score</Option>
                            <Option value="player_tscore" label="Player T-Score">Player T-Score</Option>
                            <Option value="player_pos_tscore" label="Player Position T-Score">Player Position T-Score</Option>
                            <Option value="player_team_tscore" label="Player Team T-Score">Player Team T-Score</Option>
                            <Option value="player_tscore_desc" label="Player T-Score Desc">Player T-Score Desc</Option>
                            <Option value="player_pos_tscore_desc" label="Player Position T-Score Desc">Player Position T-Score Desc</Option>
                            <Option value="player_team_tscore_desc" label="Player Team T-Score Desc">Player Team T-Score Desc</Option>
                            <Option value="player_percentile" label="Player Percentile">Player Percentile</Option>
                            <Option value="player_pos_percentile" label="Player Position Percentile">Player Position Percentile</Option>
                            <Option value="player_team_percentile" label="Player Team Percentile">Player Team Percentile</Option>
                            <Option value="player_pos_zscore_hist" label="Player Position Z-Score Hist">Player Position Z-Score Hist</Option>
                            <Option value="player_team_zscore_hist" label="Player Team Z-Score Hist">Player Team Z-Score Hist</Option>
                            <Option value="player_pos_tscore_hist" label="Player Position T-Score Hist">Player Position T-Score Hist</Option>
                            <Option value="player_team_tscore_hist" label="Player Team T-Score Hist">Player Team T-Score Hist</Option>
                            <Option value="player_pos_percentile_hist" label="Player Position Percentile Hist">Player Position Percentile Hist</Option>
                            <Option value="player_team_percentile_hist" label="Player Team Percentile Hist">Player Team Percentile Hist</Option>
                        </Select>
                    </div>
                </div>
            ) : null}

            {getSelectReportTypeObj(selectedReportTypeKey)?.requirements?.includes('emails') ? (
                <div>
                    <Text>Select email recipients for report: </Text> <br />
                    <Checkbox.Group
                        value={config.emails || []}
                        onChange={handleEmailSelectionChange}
                    >
                        <Col style={{ marginTop: "0.5em", overflowY: "auto", maxHeight: "300px" }}>
                            {getOrderedCoachEmailList().map((email, idx) => (
                                <Row key={`${email}-${idx}`}>
                                    <Checkbox key={email.key} value={email.value} style={{ marginLeft: '10px' }}>
                                        {email.label}
                                    </Checkbox>
                                </Row>
                            ))}
                        </Col>
                    </Checkbox.Group>
                </div>
            ) : null}

        </div>
    )
}

export default GeneralReportConfig